import React from "react";

import {
  Box,
  Paginator,
  EmptyState,
  CircularProgress,
  LinearProgress,
} from "@tiny/components";
import Search from "@tiny/icons/generated/Search";
import { SubscribersListProps } from "./SubscribersList.types";
import { useSubscribersList } from "./SubscribersList.hooks";
import { itemsPerPageOptions } from "./SubscribersList.helpers";
import SubscribersListHeader from "./SubscribersListHeader";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import DeleteSubscribersDialog from "./DeleteSubscribersDialog";
import SubscribersTable from "../SubscribersTable/SubscribersTable";
import FilterSubscribersDialog from "./FilterSubscribersDialog";
import MoveToAudienceDialog from "./MoveToAudienceDialog";
import { useGetCustomAttributesQuery } from "@tiny/api";

function SubscribersList(props: SubscribersListProps) {
  const {
    showChip,
    segmentId,
    enableEdit,
    onAfterDelete,
    hideSort,
    hideDelete,
    ...other
  } = props;

  const { mdUp } = useBreakpoints();

  const { data: customAttributesData } = useGetCustomAttributesQuery();
  const customAttributeNames = React.useMemo(
    () =>
      customAttributesData?.customAttributeDefinitions?.map(
        (attr) => attr.attributeName
      ) ?? [],
    [customAttributesData]
  );

  const {
    activeCustomer,
    allSubscribersSelected,
    closeDelete,
    currentPage,
    deleteOpen,
    deleteSelectedSubscribers,
    deselectAllSubscribers,
    exportSubscribers,
    handlePageChange,
    handleSizeChange,
    isLoading,
    isRefetching,
    isSearching,
    pageSize,
    searchSubscribers,
    selectSubscriber,
    selected,
    selectedSubscribersIds,
    sort,
    sortSubscribers,
    subscribers,
    successDelete,
    successEdit,
    toggleAllSubscribers,
    totalElements,
    openDelete,
    columnsSettingsVisible,
    openColumnsSettings,
    closeColumnsSettings,
    filterOpen,
    openFilter,
    closeFilter,
    saveFilters,
    filterStatuses,
    filterTags,
    deselectTag,
    deselectStatus,
    onSubscriberClick,
    enableMove,
    selectAllInAudience,
    canSelectAllInAudience,
    onSelectAllInAudience,
    clearSelection,
    moveOpen,
    openMove,
    closeMove,
    successMove,
  } = useSubscribersList(props);

  const emptyStateTitle = "No subscribers found";
  const loaderActions = () => {
    return;
  };

  const emptyStateMessage = isSearching ? (
    <span>
      Try some other search keywords.
      <br /> Tip: Try remembering the subscribers subject line or subscribers
      name.
    </span>
  ) : mdUp ? (
    <span>Click the Import button to add new subscribers.</span>
  ) : null;

  return isLoading ? (
    <Box
      {...other}
      width="100%"
      height="600px"
      display="flex"
      flexDirection="column"
    >
      <Box>
        <SubscribersListHeader
          onDeleteClick={loaderActions}
          onDeselectAll={loaderActions}
          onExportClick={loaderActions}
          onSearchChange={loaderActions}
          onSortChange={loaderActions}
          onColumnsSettingsOpen={loaderActions}
          onFilterOpen={loaderActions}
          selected={[]}
          sort={[]}
          tags={[]}
          statuses={[]}
          onDeselectTag={loaderActions}
          onDeselectStatus={loaderActions}
          hideSort={true}
          hideDelete={true}
          customAttributeNames={customAttributeNames}
        />
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex="1 1 0"
      >
        <CircularProgress />
      </Box>
    </Box>
  ) : (
    <>
      <Box {...other}>
        {isRefetching && <LinearProgress />}
        <SubscribersListHeader
          onDeleteClick={deleteSelectedSubscribers}
          onDeselectAll={deselectAllSubscribers}
          onExportClick={exportSubscribers as never}
          onSearchChange={searchSubscribers}
          onSortChange={sortSubscribers}
          onColumnsSettingsOpen={openColumnsSettings}
          onFilterOpen={openFilter}
          selected={selectedSubscribersIds}
          sort={sort}
          tags={filterTags}
          statuses={filterStatuses}
          onDeselectTag={deselectTag}
          onDeselectStatus={deselectStatus}
          hideSort={hideSort}
          hideDelete={hideDelete}
          customAttributeNames={customAttributeNames}
          showMove={enableMove}
          onMoveClick={openMove}
          selectAllInAudience={selectAllInAudience}
          canSelectAllInAudience={canSelectAllInAudience}
          onSelectAllInAudience={onSelectAllInAudience}
          onClearSelection={clearSelection}
          totalElements={totalElements}
        />
        <SubscribersTable
          subscribers={subscribers}
          selected={selectedSubscribersIds}
          enableEdit={enableEdit}
          onToggleSelect={selectSubscriber}
          allSelected={allSubscribersSelected}
          onToggleSelectAll={toggleAllSubscribers}
          onDelete={openDelete}
          onAfterEdit={successEdit}
          columnsSettingsVisible={columnsSettingsVisible}
          onColumnsSettingsClose={closeColumnsSettings}
          hideDelete={hideDelete}
          customAttributeNames={customAttributeNames}
          onRowClick={onSubscriberClick}
        />
        {subscribers.length > 0 && (
          <Paginator
            mt={mdUp ? 5 : 3}
            ml={mdUp ? 6 : 4}
            mr={mdUp ? 6 : 4}
            pb={mdUp ? 5 : 3}
            itemsPerPage={pageSize}
            itemsPerPageLabel="Results"
            itemsPerPageOptions={itemsPerPageOptions}
            onItemsPerPageChange={handleSizeChange}
            onPageChange={handlePageChange}
            page={currentPage}
            totalCount={totalElements}
          />
        )}
        {!isLoading && subscribers.length === 0 && (
          <Box bgcolor="grey.50" pt={10} pb={10}>
            <EmptyState
              title={emptyStateTitle}
              message={emptyStateMessage}
              icon={<Search />}
              iconColor="primary"
              circleColor="white"
              align="center"
            />
          </Box>
        )}
      </Box>
      <DeleteSubscribersDialog
        open={deleteOpen}
        onClose={closeDelete}
        customers={activeCustomer ? [activeCustomer] : selected}
        onSuccess={successDelete}
        segmentId={segmentId}
      />
      <FilterSubscribersDialog
        open={filterOpen}
        onClose={closeFilter}
        onSave={saveFilters}
        statuses={filterStatuses}
        tags={filterTags}
      />
      <MoveToAudienceDialog
        open={moveOpen}
        onClose={closeMove}
        sourceSegmentId={segmentId}
        selectedIds={selectedSubscribersIds}
        moveAll={selectAllInAudience}
        totalCount={totalElements}
        onSuccess={successMove}
      />
    </>
  );
}

export default SubscribersList;
